/**
 * The master output of the app: everything audible (sound effects, music, other players'
 * voices) is summed into one input and goes through a compressor and a soft clipper before
 * the speakers, so a loud moment (fanfare + music + two kids shouting) never clips.
 *
 * Built once per AudioContext by the AudioHost; the offline renders of the e2e audio checks
 * build the same chain on an OfflineAudioContext.
 */

export interface OutputChain {
  /** Connect every sound here. */
  readonly input: GainNode;
  disconnect(): void;
}

/**
 * Compressor: transparent at normal levels, acts as a limiter close to full scale. The
 * soft clipper after it guarantees the bound even for transients the compressor's attack
 * lets through (and for anything above 1.0 that reaches it).
 */
const COMPRESSOR = {
  threshold: -10,
  knee: 6,
  ratio: 12,
  attack: 0.003,
  release: 0.2,
} as const;

/** Samples above this pass unchanged; above it the curve bends smoothly towards CEILING. */
const CLIP_KNEE = 0.7;
/** The loudest sample the chain can ever produce. */
export const OUTPUT_CEILING = 0.95;

export function createOutputChain(
  ctx: BaseAudioContext,
  destination: AudioNode = ctx.destination,
): OutputChain {
  const input = ctx.createGain();
  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.value = COMPRESSOR.threshold;
  compressor.knee.value = COMPRESSOR.knee;
  compressor.ratio.value = COMPRESSOR.ratio;
  compressor.attack.value = COMPRESSOR.attack;
  compressor.release.value = COMPRESSOR.release;
  const clipper = ctx.createWaveShaper();
  clipper.curve = softClipCurve();
  clipper.oversample = 'none';
  input.connect(compressor);
  compressor.connect(clipper);
  clipper.connect(destination);
  return {
    input,
    disconnect() {
      input.disconnect();
      compressor.disconnect();
      clipper.disconnect();
    },
  };
}

/**
 * Transfer curve of the soft clipper over the input range -1..1 (a WaveShaper clamps
 * larger inputs to the end points): identity up to CLIP_KNEE, then a tanh bend that
 * approaches OUTPUT_CEILING.
 */
export function softClipCurve(points = 2049): Float32Array<ArrayBuffer> {
  const curve = new Float32Array(points);
  const room = OUTPUT_CEILING - CLIP_KNEE;
  for (let i = 0; i < points; i++) {
    const x = (i / (points - 1)) * 2 - 1;
    const magnitude = Math.abs(x);
    const y =
      magnitude <= CLIP_KNEE
        ? magnitude
        : CLIP_KNEE + room * Math.tanh((magnitude - CLIP_KNEE) / room);
    curve[i] = x < 0 ? -y : y;
  }
  return curve;
}
